import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TablePagination,
  CircularProgress,
  Button,
  useTheme,
  Fade,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  ListItemText,
  OutlinedInput,
  alpha,
  Badge
} from '@mui/material';
import { BRANDING } from '../../utils/branding';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfToday, startOfYesterday, startOfYear } from 'date-fns';
import {
  Assignment,
  People,
  Home,
  AccountBalance,
  PersonAdd,
  Business,
  Groups,
  LocationOn,
  Close,
  MonetizationOn,
  TrendingUp,
  NotificationsActive,
  Stars,
  EmojiEvents as RewardIcon,
  ChevronLeft,
  ChevronRight,
  CurrencyRupee,
} from '@mui/icons-material';

// --- Futuristic UI Components ---

const GlassCard = ({ children, sx, onClick, ...props }) => (
  <Card
    {...props}
    onClick={onClick}
    sx={{
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(20px)',
      borderRadius: 4,
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': {
        transform: onClick ? 'translateY(-5px)' : 'none',
        boxShadow: onClick ? '0 12px 40px 0 rgba(31, 38, 135, 0.15)' : '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
        borderColor: 'primary.main',
      },
      ...sx,
    }}
  >
    {children}
  </Card>
);

const AnimatedCounter = ({ value, duration = 2500 }) => {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setDisplayValue(value);
      return;
    }

    const str = String(value);
    const origMatch = str.match(/[\d.,]+/);
    if (!origMatch) {
      setDisplayValue(value);
      return;
    }

    const startIdx = str.indexOf(origMatch[0]);
    const endIdx = startIdx + origMatch[0].length;
    const prefix = str.substring(0, startIdx);
    const suffix = str.substring(endIdx);
    const numStr = origMatch[0];

    const cleanNumStr = numStr.replace(/,/g, '');
    const targetNum = parseFloat(cleanNumStr);
    if (isNaN(targetNum)) {
      setDisplayValue(value);
      return;
    }

    const hasComma = numStr.includes(',');
    const decimalIdx = numStr.indexOf('.');
    const decimals = decimalIdx !== -1 ? numStr.length - decimalIdx - 1 : 0;

    let start = 0;
    const startTime = performance.now();
    let animationFrameId;

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      
      // Easing: easeOutExpo
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentNum = start + easeProgress * (targetNum - start);

      let formattedNum;
      if (hasComma) {
        formattedNum = new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(currentNum);
      } else {
        formattedNum = currentNum.toFixed(decimals);
      }

      setDisplayValue(`${prefix}${formattedNum}${suffix}`);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [value, duration]);

  return <>{displayValue}</>;
};

const StatCard = ({ title, value, icon: Icon, color, loading, onClick, trend }) => {
  return (
    <GlassCard onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: 0.5,
                display: 'block',
                lineHeight: 1.2,
                fontSize: '0.65rem',
                whiteSpace: 'normal',
                overflow: 'visible',
                wordBreak: 'break-word',
                minHeight: '2.4em', // Reserve space for 2 lines to keep alignment
              }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width="70%" height={32} sx={{ mt: 1 }} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: '#1a1f2c',
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.4rem' },
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    lineHeight: 1.2
                  }}
                >
                  <AnimatedCounter value={value} />
                </Typography>
                {trend && (
                  <Typography variant="caption" sx={{ color: trend.startsWith('+') ? 'success.main' : 'error.main', fontWeight: 700, display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
                    <TrendingUp sx={{ fontSize: 12, mr: 0.5, display: trend.startsWith('+') ? 'block' : 'none' }} />
                    {trend}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)`,
              color: '#fff',
              width: { xs: 36, sm: 44 },
              height: { xs: 36, sm: 44 },
              borderRadius: { xs: 1.5, sm: 2.5 },
              boxShadow: `0 4px 12px 0 ${color}30`,
              flexShrink: 0,
              ml: 1
            }}
          >
            <Icon sx={{ fontSize: { xs: 18, sm: 22 } }} />
          </Avatar>
        </Box>
      </CardContent>
    </GlassCard>
  );
};

const getRewardImageSrc = (benefitValue) => {
  const val = benefitValue?.toString().toLowerCase() || '';
  if (val.includes('mobile')) return require('../../images/reward_mobile_3d.png');
  if (val.includes('laptop')) return require('../../images/reward_laptop_3d.png');
  if (val.includes('bike') || val.includes('scooty')) {
      if (val.includes('55k') || val.includes('55,000') || val.includes('55000')) {
          return require('../../images/reward_cheap_bike_3d.png');
      }
      return require('../../images/reward_bike_3d.png');
  }
  if (val.includes('house') || val.includes('villa') || val.includes('flat')) return require('../../images/reward_house_3d.png');
  if (val.includes('share') || val.includes('gold')) return require('../../images/reward_shares_3d.png');
  
  if (val.includes('car')) {
    if (val.includes('20,00,000') || val.includes('2000000') || val.includes('20l')) {
        return require('../../images/car_luxury_3d.png');
    }
    if (val.includes('15,00,000') || val.includes('1500000') || val.includes('15l')) {
        return require('../../images/car_fortuner_3d.png');
    }
    if (val.includes('10,00,000') || val.includes('1000000') || val.includes('10l')) {
        return require('../../images/car_harrier_3d.png');
    }
    if (val.includes('7,00,000') || val.includes('700000') || val.includes('7l')) {
        return require('../../images/reward_car_3d.png');
    }
    if (val.includes('3,00,000') || val.includes('300000') || val.includes('3l')) {
        return require('../../images/car_kiger_3d.png');
    }
    if (val.includes('1.5') || val.includes('1,50,000') || val.includes('150000')) {
        return require('../../images/reward_cheap_car_3d.png');
    }
  }
  return require('../../images/reward_car_3d.png');
};

export default function RealEstateDashboard() {
  const history = useHistory();
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = data?.loginUserType === 'admin';
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const [projectsList, setProjectsList] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('all');

  // Progression Slider State
  const [rewardIndex, setRewardIndex] = useState(0);
  const [incentiveIndex, setIncentiveIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');

  // Progression Reveal State
  const [revealedCards, setRevealedCards] = useState(() => {
    const obj = {};
    try {
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith('revealed_card_')) {
          obj[k.replace('revealed_card_', '')] = window.sessionStorage.getItem(k) === 'true';
        }
      }
    } catch (e) {
      console.error(e);
    }
    return obj;
  });

  // Details Modal State
  const [progressionModalData, setProgressionModalData] = useState(null);



  // Revenue Modal State
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueError, setRevenueError] = useState(null);
  const [revenuePagination, setRevenuePagination] = useState({
    totalProjects: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10
  });

  const [startDate, setStartDate] = useState(format(subDays(new Date(), 15), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateRangeOption, setDateRangeOption] = useState('Last 15 Days');

  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    setDateRangeOption(value);

    const today = new Date();
    let start = today;
    let end = today;

    if (value === 'Custom Range') return;

    switch (value) {
      case 'Today':
        start = startOfToday();
        end = today;
        break;
      case 'Yesterday':
        start = startOfYesterday();
        end = startOfYesterday();
        break;
      case 'Last 7 Days':
        start = subDays(today, 7);
        end = today;
        break;
      case 'Last 15 Days':
        start = subDays(today, 15);
        end = today;
        break;
      case 'Last 30 Days':
        start = subDays(today, 30);
        end = today;
        break;
      case 'This Month':
        start = startOfMonth(today);
        end = today;
        break;
      case 'Last Month':
        const lastMonth = subMonths(today, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        break;
      case 'Last 6 Months':
        start = startOfMonth(subMonths(today, 5));
        end = today;
        break;
      case 'This Year':
        start = startOfYear(today);
        end = today;
        break;
      default:
        break;
    }

    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects?limit=1000`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const result = await response.json();
        if (result.success && result.servicesResponse) {
          setProjectsList(result.servicesResponse);
        }
      } catch (err) {
        console.error('Failed to load projects list', err);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const queryParams = new URLSearchParams({
          startDate,
          endDate
        }).toString();

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const result = await response.json();

        if (result.success) {
          setData(result.data);
          if (result.data.levelProgress) {
            setRewardIndex(result.data.levelProgress.rewardProgressionActiveIndex || 0);
            setIncentiveIndex(result.data.levelProgress.incentiveProgressionActiveIndex || 0);
          }
          if (result.data.projectInventoryStats?.length > 0) {
            setSelectedProjectId(result.data.projectInventoryStats[0].projectId);
          }
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err) {
        console.error("API Error:", err);
        setError('Network error: Could not fetch data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const fetchRevenueData = async (page = 1, limit = 10) => {
    try {
      setRevenueLoading(true);
      setRevenueError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/reports/projectWiseReceived?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const result = await response.json();
      if (result.success) {
        setRevenueData(result.data);
        setRevenuePagination(result.pagination);
      } else {
        setRevenueError('Failed to load revenue data');
      }
    } catch (err) {
      setRevenueError('Network error: Could not fetch revenue data.');
    } finally {
      setRevenueLoading(false);
    }
  };

  const handleRevenueModalOpen = () => {
    setRevenueModalOpen(true);
    fetchRevenueData(1, 10);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return formatCurrency(amount);
  };

  // --- Chart Configurations ---

  const revenueChartOptions = {
    chart: {
      id: 'revenue-trend',
      toolbar: { show: false },
      sparkline: { enabled: false },
      background: 'transparent',
      animations: { enabled: false },
    },
    colors: [theme.palette.primary.main, theme.palette.secondary.main],
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [20, 100, 100, 100]
      }
    },
    xaxis: {
      categories: data?.graphs?.revenue?.labels || [],
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { show: false } },
    grid: { borderColor: 'rgba(0,0,0,0.05)', strokeDashArray: 4 },
    tooltip: { theme: 'light', x: { show: true } },
    dataLabels: { enabled: false }
  };

  // Mock series for visual effect if real trend data is missing
  const revenueSeries = [
    {
      name: 'Revenue',
      data: data?.graphs?.revenue?.values || []
    }
  ];

  const distributionOptions = {
    chart: {
      type: 'donut',
      toolbar: { show: false },
      animations: { enabled: false }
    },
    labels: ['Confirmed', 'Pending', 'Cancelled'],
    colors: ['#22c55e', '#f59e0b', '#ef4444'],
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '13px',
      fontWeight: 500,
      markers: { radius: 12 },
      itemMargin: { horizontal: 10, vertical: 8 },
      onItemClick: { toggleDataSeries: true },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              fontWeight: 600,
              offsetY: -5
            },
            value: {
              show: true,
              fontSize: '18px',
              fontWeight: 800,
              offsetY: 5,
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '12px',
              color: '#64748b',
              formatter: () => {
                const total = (data?.totalBooking || 0) + (data?.pendingBookingCount || 0) + (data?.cancelledBookingCount || 0);
                return total;
              }
            }
          }
        }
      }
    },
    dataLabels: { enabled: false },
    stroke: { width: 0 }
  };

  const leadsBarOptions = {
    chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '40%',
        distributed: true,
      }
    },
    colors: ['#673ab7', '#00bcd4', '#ff5722'],
    xaxis: {
      categories: ['Leads', 'Visits', 'Bookings'],
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { show: false },
    grid: { show: false },
    legend: { show: false },
    dataLabels: { enabled: true }
  };

  const sellPurchaseOptions = {
    ...distributionOptions,
    labels: ['Total Sell', 'Total Purchase'],
    colors: ['#8b5cf6', '#14b8a6'],
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              fontWeight: 600,
              offsetY: -8
            },
            value: {
              show: true,
              fontSize: '14px',
              fontWeight: 800,
              offsetY: 4,
              formatter: (val) => formatCurrencyShort(val)
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '11px',
              color: '#64748b',
              offsetY: 18,
              formatter: (w) => {
                return formatCurrencyShort(
                  w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                );
              }
            }
          }
        }
      }
    }
  };

  const getInventoryGraphData = () => {
    let available = 0;
    let booked = 0;
    let hold = 0;
    let unselect = 0;

    const inventoryStatsList = data?.projectInventoryStats || [];

    const filteredProjects = selectedProjectId !== 'all'
      ? inventoryStatsList.filter(p => p.projectId === selectedProjectId)
      : inventoryStatsList;

    filteredProjects.forEach(p => {
      available += (p.totalAvailable || 0);
      booked += (p.totalBooked || 0);
      hold += (p.totalHold || 0);
      unselect += (p.totalNotSelected || 0);
    });

    return [available, booked, hold, unselect];
  };

  const inventoryDataArray = getInventoryGraphData();

  const inventoryChartOptions = {
    ...distributionOptions,
    labels: ['Available', 'Booked', 'Hold', 'Not Selected'],
    colors: ['#3b82f6', '#10b981', '#f59e0b', '#94a3b8'],
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: { show: true, fontSize: '14px', fontWeight: 600, offsetY: -5 },
            value: { show: true, fontSize: '18px', fontWeight: 800, offsetY: 5 },
            total: {
              show: true,
              label: 'Total',
              fontSize: '12px',
              color: '#64748b',
              formatter: () => inventoryDataArray.reduce((a, b) => a + b, 0)
            }
          }
        }
      }
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, md: 4 },
      maxWidth: 1600,
      margin: '0 auto',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',

    }}>

      {/* Dynamic Header */}
      <Box sx={{ mb: 6, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 2 }}>
        <Box>
          <Fade in timeout={800}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: '#1a1f2c', mb: 1, letterSpacing: -1 }}>
                Insight <Box component="span" sx={{ color: 'primary.main' }}>Dashboard</Box>
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActive sx={{ fontSize: 18, color: 'primary.main' }} />
                Real-time performance metrics for {BRANDING.name} Projects.
              </Typography>
            </Box>
          </Fade>
        </Box>
        <Paper sx={{
          p: { xs: 2, sm: 2 },
          borderRadius: 4,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 2,
          background: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,0,0,0.05)',
          width: { xs: '100%', md: 'auto' }
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter by Date</InputLabel>
              <Select
                value={dateRangeOption}
                label="Filter by Date"
                onChange={handleDateRangeChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Yesterday">Yesterday</MenuItem>
                <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                <MenuItem value="Last 15 Days">Last 15 Days</MenuItem>
                <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                <MenuItem value="This Month">This Month</MenuItem>
                <MenuItem value="Last Month">Last Month</MenuItem>
                <MenuItem value="Last 6 Months">Last 6 Months</MenuItem>
                <MenuItem value="This Year">This Year</MenuItem>
                <MenuItem value="Custom Range">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {dateRangeOption === 'Custom Range' && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                />
                <TextField
                  label="End"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                />
              </Stack>
            )}
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'space-between', md: 'flex-end' },
            borderLeft: { xs: 'none', md: '1px solid rgba(0,0,0,0.1)' },
            pl: { xs: 0, md: 2 }
          }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>SYSTEM TIME</Typography>
              <Typography variant="h6" sx={{ color: '#1a1f2c', lineHeight: 1, fontSize: '1rem' }}>
                {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'primary.main', borderRadius: 2, width: 36, height: 36 }}>
              <Business sx={{ fontSize: 20 }} />
            </Avatar>
          </Box>
        </Paper>
      </Box>

      {/* Primary Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={12} md={4}>
          <StatCard
            title="Total Revenue (Booking)"
            value={data ? formatCurrency(data.totalRevenue) : '₹0'}
            icon={AccountBalance}
            color="#6366f1"
            loading={loading}
            onClick={handleRevenueModalOpen}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Total Bookings"
            value={data?.totalBooking || 0}
            icon={Assignment}
            color="#22c55e"
            loading={loading}
            onClick={() => history.push('/app/booking/BookingDetail')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Total Leads"
            value={data?.leads || 0}
            icon={People}
            color="#ec4899"
            loading={loading}
            onClick={() => history.push('/app/lead/LeadList')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Hot Leads"
            value={data?.hotLeads || 0}
            icon={TrendingUp}
            color="#f43f5e"
            loading={loading}
            onClick={() => history.push({ pathname: '/app/lead/LeadList', state: { statusFilter: 'HOT' } })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Site Visits"
            value={data?.siteVisits || 0}
            icon={LocationOn}
            color="#f59e0b"
            loading={loading}
            onClick={() => history.push('/app/siteVisit/SiteVisit')}
          />
        </Grid>
      </Grid>

      {/* Main Content: Charts & Analytics */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Top Row: Revenue Trend & Conversion Funnel */}
        <Grid item xs={12} lg={8}>
          <GlassCard sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Revenue Growth</Typography>
                <Typography variant="caption" color="text.secondary">Projection vs Actual Performance</Typography>
              </Box>
              <Button size="small" variant="text" sx={{ borderRadius: 2 }}>Historical Data</Button>
            </Box>
            <Box sx={{ height: { xs: 220, md: 240 }, mt: 1 }}>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 4 }} />
              ) : (
                <ReactApexChart
                  options={revenueChartOptions}
                  series={revenueSeries}
                  type="area"
                  height="100%"
                />
              )}
            </Box>
          </GlassCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <GlassCard sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Conversion Funnel</Typography>
            <Box sx={{ height: { xs: 220, md: 240 }, mt: 1 }}>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 4 }} />
              ) : (
                <ReactApexChart
                  options={leadsBarOptions}
                  series={[{
                    data: [data?.leads || 0, data?.siteVisits || 0, data?.totalBooking || 0]
                  }]}
                  type="bar"
                  height="100%"
                />
              )}
            </Box>
          </GlassCard>
        </Grid>

        {/* Bottom Row: Donuts — Admin only */}
        {isAdmin && (
        <Grid item xs={12} lg={4}>
          <GlassCard sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Booking Status</Typography>
            <Box sx={{ height: { xs: 220, md: 240 }, display: 'flex', justifyContent: 'center', mt: 1 }}>
              {loading ? (
                <Skeleton variant="circular" width={260} height={260} sx={{ mx: 'auto' }} />
              ) : (
                <ReactApexChart
                  options={{
                    ...distributionOptions,
                    legend: {
                      ...distributionOptions.legend,
                      formatter: (val, opts) => `${val}: ${opts.w.globals.series[opts.seriesIndex]}`
                    }
                  }}
                  series={[
                    data?.totalBooking || 0,
                    data?.pendingBookingCount || 0,
                    data?.cancelledBookingCount || 0
                  ]}
                  type="donut"
                  width="100%"
                />
              )}
            </Box>
          </GlassCard>
        </Grid>
        )}

        {isAdmin && (
        <Grid item xs={12} lg={4}>
          <GlassCard sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Sell vs Purchase</Typography>
            <Box sx={{ height: { xs: 220, md: 240 }, display: 'flex', justifyContent: 'center', mt: 1 }}>
              {loading ? (
                <Skeleton variant="circular" width={260} height={260} sx={{ mx: 'auto' }} />
              ) : (
                <ReactApexChart
                  options={{
                    ...sellPurchaseOptions,
                    legend: {
                      ...sellPurchaseOptions.legend,
                      formatter: (val, opts) => `${val}: ${formatCurrency(opts.w.globals.series[opts.seriesIndex])}`
                    }
                  }}
                  series={[
                    data?.totalSellingAmount || 0,
                    data?.totalPurchaseAmount || 0
                  ]}
                  type="donut"
                  width="100%"
                />
              )}
            </Box>
          </GlassCard>
        </Grid>
        )}

        {isAdmin && (
        <Grid item xs={12} lg={4}>
          <GlassCard sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Project Inventory</Typography>
            </Box>
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel id="project-select-label">Select Project</InputLabel>
              <Select
                labelId="project-select-label"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                input={<OutlinedInput label="Select Project" sx={{ borderRadius: 2 }} />}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 8.5,
                    },
                  },
                }}
              >
                {(data?.projectInventoryStats || []).map((p) => (
                  <MenuItem key={p.projectId} value={p.projectId}>
                    <ListItemText primary={p.projectName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ height: { xs: 220, md: 240 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {loading ? (
                <Skeleton variant="circular" width={260} height={260} sx={{ mx: 'auto' }} />
              ) : (
                <ReactApexChart
                  options={{
                    ...inventoryChartOptions,
                    legend: {
                      ...inventoryChartOptions.legend,
                      formatter: (val, opts) => `${val}: ${opts.w.globals.series[opts.seriesIndex]}`
                    }
                  }}
                  series={inventoryDataArray}
                  type="donut"
                  width="100%"
                />
              )}
            </Box>
          </GlassCard>
        </Grid>
        )}
      </Grid>

      {/* Secondary Stats & Recent Activity */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
           // { key: 'totalBookedAmount', label: 'Total Booked', icon: MonetizationOn, color: '#8b5cf6', link: '/app/booking/BookingDetail' },
          { key: 'availablePlots', label: 'Total Available Plots', icon: Home, color: '#f43f5e', link: '/app/project/PlotListDetails' },
          { key: 'projects', label: 'Active Projects', icon: Business, color: '#6366f1', link: '/app/project/ProjectList' },
          { key: 'activeAssociates', label: 'Active Associates', icon: People, color: '#10b981', link: '/app/users/AssociateList', state: { tabValue: 1 } },
          { key: 'associates', label: 'Total Associates', icon: PersonAdd, color: '#3b82f6', link: '/app/users/AssociateList' },
        ].map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.key} sx={{ display: 'flex' }}>
            <StatCard
              title={stat.label}
              value={stat.key.includes('Amount') ? formatCurrency(data?.[stat.key] || 0) : (data?.[stat.key] || 0)}
              icon={stat.icon}
              color={stat.color}
              loading={loading}
              onClick={() => {
                if (stat.state) {
                  history.push({ pathname: stat.link, state: stat.state });
                } else {
                  history.push(stat.link);
                }
              }}
            />
          </Grid>
        ))}
      </Grid>
      
      {/* Professional Performance Dashboard */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 6, position: 'relative', width: '100%' }}>
        {loading ? (
          <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 4, width: '100%' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 4, width: '100%' }} />
            </Grid>
          </Grid>
        ) : (
          <>
            {((data?.levelProgress?.rewardProgressionCards?.length || 0) > 1 || (data?.levelProgress?.incentiveProgressionCards?.length || 0) > 1) && (
              <IconButton 
                sx={{ 
                  position: 'absolute', left: { xs: -10, md: -20 }, zIndex: 2,
                  bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' }
                }}
                disabled={rewardIndex === 0 && incentiveIndex === 0}
                onClick={() => {
                  setSlideDirection('right');
                  setRewardIndex(prev => Math.max(0, prev - 1));
                  setIncentiveIndex(prev => Math.max(0, prev - 1));
                }}
              >
                <ChevronLeft />
              </IconButton>
            )}
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              {[
                {
                  type: 'Reward',
                  cards: data?.levelProgress?.rewardProgressionCards || [],
                  currentIndex: rewardIndex,
                  setIndex: setRewardIndex,
                  color: '#6366f1',
                  bg: alpha('#6366f1', 0.02),
                  icon: <Stars />,
                  benefitIcon: <RewardIcon />,
                  benefitLabel: 'Reward',
                },
                {
                  type: 'Incentive',
                  cards: data?.levelProgress?.incentiveProgressionCards || [],
                  currentIndex: incentiveIndex,
                  setIndex: setIncentiveIndex,
                  color: '#f59e0b',
                  bg: alpha('#f59e0b', 0.02),
                  icon: <CurrencyRupee />,
                  benefitIcon: <TrendingUp />,
                  benefitLabel: 'Cash Bonus',
                }
              ].map((config, idx) => {
                // Fallback to the old single progression object if cards array is empty to prevent breaking
                const fallbackProgression = config.type === 'Reward' ? data?.levelProgress?.rewardProgression : data?.levelProgress?.incentiveProgression;
                const cardsToRender = config.cards.length > 0 ? config.cards : [fallbackProgression || {}];

                const currentProgression = cardsToRender[config.currentIndex] || cardsToRender[0];
                const currentBenefitValue = config.type === 'Reward' 
                  ? (currentProgression?.rewardDescription || currentProgression?.benefitLabel) 
                  : (currentProgression?.incentiveAmount ? `₹${currentProgression.incentiveAmount}` : '₹0');
                const overlayImageSrc = config.type === 'Reward' 
                  ? getRewardImageSrc(currentBenefitValue)
                  : require('../../images/cash_bonus_inr_3d.png');

                return (
                <Grid item xs={12} md={6} key={idx}>
                  <GlassCard sx={{ p: 0, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)', position: 'relative', minHeight: 280 }}>
                    
                    <Box 
                      sx={{ 
                        display: 'flex',
                        width: '100%',
                        transition: 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)',
                        transform: `translateX(-${config.currentIndex * 100}%)`,
                        opacity: 1
                      }}
                    >
                      {cardsToRender.map((progression, pIdx) => {
                        const benefitValue = config.type === 'Reward' 
                          ? (progression?.rewardDescription || progression?.benefitLabel) 
                          : (progression?.incentiveAmount);

                        const imageSrc = config.type === 'Reward' 
                          ? getRewardImageSrc(benefitValue)
                          : require('../../images/cash_bonus_inr_3d.png');

                        const cardKey = `${config.type}_${progression?.rankName || ''}`;
                        
                        // An upcoming card is revealed only if it is in sessionStorage / state
                        // If it's not an upcoming card (e.g. achieved), it is revealed by default
                        const isThisCardRevealed = !progression?.isUpcoming || !!revealedCards[cardKey];

                        // Check if the reward or incentive is empty/0 to avoid displaying place-holder image
                        const benefitValueStr = benefitValue?.toString().trim() || '';
                        const isRewardEmpty = config.type === 'Reward' && (
                          !benefitValueStr ||
                          benefitValueStr === '0' ||
                          benefitValueStr.toLowerCase() === 'n/a' ||
                          benefitValueStr.toLowerCase() === 'nothing' ||
                          benefitValueStr.toLowerCase() === 'none' ||
                          benefitValueStr.toLowerCase() === 'reward'
                        );
                        const isIncentiveEmpty = config.type === 'Incentive' && (
                          !benefitValueStr ||
                          parseFloat(benefitValueStr.replace(/[^0-9.]/g, '')) === 0 ||
                          isNaN(parseFloat(benefitValueStr.replace(/[^0-9.]/g, '')))
                        );
                        const isBenefitEmpty = isRewardEmpty || isIncentiveEmpty;
                        
                        // We do NOT show the image on the active/current level card (isCurrent: true)
                        // For all other cards, we show the image once they are revealed and not empty
                        const showImage = !progression?.isCurrent && isThisCardRevealed && !isBenefitEmpty;

                        return (
                          <Box 
                            key={pIdx} 
                            onClick={() => {
                              setProgressionModalData({ ...progression, type: config.type, benefitValue: benefitValue });
                            }}
                            sx={{ 
                              width: '100%', minWidth: '100%', maxWidth: '100%', flexShrink: 0, overflow: 'hidden', boxSizing: 'border-box',
                              cursor: 'pointer', transition: 'background-color 0.3s ease',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                              position: 'relative'
                            }}
                          >
                            {/* PER-CARD REVEAL OVERLAY (Only shows if card is upcoming and not revealed yet) */}
                            {!isThisCardRevealed && (
                              <Box 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  try {
                                    window.sessionStorage.setItem(`revealed_card_${cardKey}`, 'true');
                                  } catch (err) {
                                    console.error(err);
                                  }
                                  setRevealedCards(prev => ({ ...prev, [cardKey]: true }));
                                }}
                                sx={{
                                  position: 'absolute', inset: 0, zIndex: 10, cursor: 'pointer',
                                  bgcolor: '#ffffff',
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                  overflow: 'hidden'
                                }}
                              >
                                 <img 
                                    src={imageSrc} 
                                    alt="Reveal"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }}
                                 />
                                 <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 50%)' }} />
                                 <Box sx={{ position: 'absolute', bottom: 32, zIndex: 2, textAlign: 'center', left: 0, right: 0 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: 'white', textShadow: '0 2px 10px rgba(0,0,0,0.5)', mb: 1 }}>
                                       {config.type} Unlocked
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'white', bgcolor: config.color, px: 2, py: 0.5, borderRadius: 4, display: 'inline-block', boxShadow: '0 4px 12px ' + config.color + '30' }}>
                                       Click to Reveal
                                    </Typography>
                                 </Box>
                              </Box>
                            )}
                            <Box sx={{ p: 1.5, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box>
                                  <Badge color="error" variant="dot" invisible={progression?.cardStatus !== 'upcoming'} sx={{ '& .MuiBadge-badge': { right: -10, top: 4 } }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>{config.type} Progression</Typography>
                                  </Badge>
                                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {progression?.rankName || 'N/A'}
                                    {progression?.cardStatus === 'achieved' && <Chip label="Achieved" size="small" color="success" sx={{ height: 20, fontSize: '10px', fontWeight: 800 }} />}
                                    {progression?.cardStatus === 'upcoming' && <Chip label="Upcoming" size="small" color="default" sx={{ height: 20, fontSize: '10px', fontWeight: 800 }} />}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ bgcolor: config.color, width: 28, height: 28 }}>{React.cloneElement(config.icon, { sx: { fontSize: 16 } })}</Avatar>
                              </Box>
                            </Box>

                            <Box sx={{ p: 2 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Box sx={{ width: '100%', maxWidth: 200 }}>
                                    <ReactApexChart
                                      options={{
                                        chart: { type: 'radialBar', sparkline: { enabled: true }, animations: { enabled: false } },
                                        plotOptions: {
                                          radialBar: {
                                            startAngle: -90, endAngle: 90,
                                            hollow: { size: '60%' },
                                            track: { background: '#f1f5f9', strokeWidth: '100%' },
                                            dataLabels: {
                                              name: { show: false },
                                              value: { offsetY: -2, fontSize: '18px', fontWeight: 900, color: '#1e293b', formatter: (val) => val + '%' }
                                            }
                                          }
                                        },
                                        colors: [config.color],
                                        labels: [progression?.rankName || 'Progress'],
                                        stroke: { lineCap: 'round' }
                                      }}
                                      series={[Number(Math.round(((progression?.selfBusiness?.percentage || 0) + (progression?.teamBusiness?.percentage || 0)) / 2)) || 0]}
                                      type="radialBar"
                                      height={160}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: -4, fontWeight: 800, color: 'text.secondary', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                    {/* {Number(Math.round(((progression?.selfBusiness?.percentage || 0) + (progression?.teamBusiness?.percentage || 0)) / 2)) || 0}% COMPLETION */}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} sm={7}>
                                  <Stack spacing={1.5}>
                                    <Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Self Business</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                          {progression?.selfBusiness?.percentage || 0}%{progression?.selfBusiness?.percentage >= 100 ? '+' : ''}
                                        </Typography>
                                      </Box>
                                      <LinearProgress variant="determinate" value={Math.min(100, progression?.selfBusiness?.percentage || 0)} sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: config.color } }} />
                                      <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        Achieved: ₹{progression?.selfBusiness?.achieved?.toLocaleString('en-IN') || 0} / ₹{progression?.selfBusiness?.target?.toLocaleString('en-IN') || 0}
                                        {(progression?.selfBusiness?.percentage >= 100 && progression?.teamBusiness?.percentage < 100) && (
                                          <Box component="span" sx={{ color: 'warning.dark', fontWeight: 800, fontSize: '11px', bgcolor: 'warning.light', px: 0.5, borderRadius: 0.5, ml: 0.5 }}>
                                            WAITING FOR TEAM TARGET
                                          </Box>
                                        )}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Team Business</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                          {progression?.teamBusiness?.percentage || 0}%{progression?.teamBusiness?.percentage >= 100 ? '+' : ''}
                                        </Typography>
                                      </Box>
                                      <LinearProgress variant="determinate" value={Math.min(100, progression?.teamBusiness?.percentage || 0)} sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }} />
                                      <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        Achieved: ₹{progression?.teamBusiness?.achieved?.toLocaleString('en-IN') || 0} / ₹{progression?.teamBusiness?.target?.toLocaleString('en-IN') || 0}
                                        
                                        {(progression?.teamBusiness?.percentage >= 100 && progression?.selfBusiness?.percentage < 100) && (
                                          <Box component="span" sx={{ color: 'warning.dark', fontWeight: 800, fontSize: '11px', bgcolor: 'warning.light', px: 0.5, borderRadius: 0.5, ml: 0.5 }}>
                                            WAITING FOR SELF TARGET
                                          </Box>
                                        )}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Box>

                            <Box sx={{ px: 2, py: 1.5, bgcolor: config.bg, borderTop: '1px solid rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ color: config.color, display: 'flex' }}>{config.benefitIcon}</Box>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', lineHeight: 1 }}>{config.benefitLabel}</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 800, color: config.color }}>
                                     {benefitValue}
                                     {config.type === 'Incentive' && progression?.incentivePayoutMonths > 0 && (
                                       <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 700, fontSize: '0.7em' }}>
                                          ({progression.incentivePayoutMonths} Months)
                                       </Typography>
                                     )}
                                  </Typography>
                                </Box>
                              </Box>
                              {showImage ? (
                                <Avatar 
                                  src={imageSrc} 
                                  sx={{ width: 48, height: 48, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', bgcolor: 'white' }} 
                                  variant="rounded"
                                />
                              ) : (
                                <Box sx={{ width: 48, height: 48 }} />
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </GlassCard>
                </Grid>
              )})}
            </Grid>
            {((data?.levelProgress?.rewardProgressionCards?.length || 0) > 1 || (data?.levelProgress?.incentiveProgressionCards?.length || 0) > 1) && (
              <IconButton 
                sx={{ 
                  position: 'absolute', right: { xs: -10, md: -20 }, zIndex: 2,
                  bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' }
                }}
                disabled={
                  rewardIndex === Math.max(0, (data?.levelProgress?.rewardProgressionCards?.length || 1) - 1) &&
                  incentiveIndex === Math.max(0, (data?.levelProgress?.incentiveProgressionCards?.length || 1) - 1)
                }
                onClick={() => {
                  setSlideDirection('left');
                  const maxReward = Math.max(0, (data?.levelProgress?.rewardProgressionCards?.length || 1) - 1);
                  const maxIncentive = Math.max(0, (data?.levelProgress?.incentiveProgressionCards?.length || 1) - 1);
                  setRewardIndex(prev => Math.min(maxReward, prev + 1));
                  setIncentiveIndex(prev => Math.min(maxIncentive, prev + 1));
                }}
              >
                <ChevronRight />
              </IconButton>
            )}

            {/* CENTRALIZED SLIDER DOTS */}
            {((data?.levelProgress?.rewardProgressionCards?.length || 0) > 1 || (data?.levelProgress?.incentiveProgressionCards?.length || 0) > 1) && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 1.5, 
                position: 'absolute',
                bottom: -28,
                left: 0,
                right: 0,
                zIndex: 5,
                opacity: 1,
                transition: 'opacity 0.6s ease'
              }}>
                {Array.from({ length: Math.max(data?.levelProgress?.rewardProgressionCards?.length || 0, data?.levelProgress?.incentiveProgressionCards?.length || 0) }).map((_, dotIdx) => (
                  <Box
                    key={dotIdx}
                    onClick={() => {
                      const maxReward = Math.max(0, (data?.levelProgress?.rewardProgressionCards?.length || 1) - 1);
                      const maxIncentive = Math.max(0, (data?.levelProgress?.incentiveProgressionCards?.length || 1) - 1);
                      setRewardIndex(Math.min(maxReward, dotIdx));
                      setIncentiveIndex(Math.min(maxIncentive, dotIdx));
                    }}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: rewardIndex === dotIdx ? 'primary.main' : 'rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: rewardIndex === dotIdx ? 'scale(1.2)' : 'scale(1)',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        opacity: 0.8
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Recent Bookings Table - Modernized */}
      <Fade in timeout={1200}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1a1f2c' }}>
              Recent Transactions
            </Typography>
            <Button variant="outlined" sx={{ borderRadius: 3 }} onClick={() => history.push('/app/booking/BookingDetail')}>View All</Button>
          </Box>
          <TableContainer component={GlassCard} sx={{ borderRadius: 4, overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Plot Details</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.recentBookings?.length > 0 ? (
                  data.recentBookings.map((row) => (
                    <TableRow
                      key={row.bookingId}
                      hover
                      onClick={() => history.push('/app/booking/BookingDetail', { bookingId: row._id })}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(99, 102, 241, 0.06) !important',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{row.bookingId}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.light' }}>
                            {row.customer.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>{row.customer}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={`Plot ${row.plotNo}`} size="small" sx={{ borderRadius: 1.5, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{formatCurrency(row.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            borderRadius: 1.5,
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            bgcolor: row.status.toLowerCase() === 'confirmed' ? 'success.light' : 'warning.light',
                            color: row.status.toLowerCase() === 'confirmed' ? 'success.dark' : 'warning.dark',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} sx={{ mb: 1, display: loading ? 'block' : 'none', mx: 'auto' }} />
                      <Typography variant="body2" color="text.secondary">
                        {loading ? 'Decrypting live data...' : 'No data records found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Fade>

      {/* Legacy Modals Kept for Functionality */}
      <Dialog open={revenueModalOpen} onClose={() => setRevenueModalOpen(false)} maxWidth="md" fullWidth sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Revenue Matrix</Typography>
          <IconButton onClick={() => setRevenueModalOpen(false)} sx={{ color: 'white' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {revenueLoading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revenueData.map((project) => (
                    <TableRow key={project._id} hover>
                      <TableCell>{project.projectName}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(project.totalReceived)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={revenuePagination.totalProjects}
                page={revenuePagination.currentPage - 1}
                onPageChange={(e, p) => fetchRevenueData(p + 1)}
                rowsPerPage={revenuePagination.limit}

              />
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog 
        open={Boolean(progressionModalData)} 
        onClose={() => setProgressionModalData(null)}
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', p: 0, minWidth: { xs: '90vw', sm: 400 }, position: 'relative' } }}
        TransitionComponent={Fade}
        transitionDuration={400}
      >
        {(() => {
          const rewardVal = progressionModalData?.rewardDescription || progressionModalData?.benefitLabel || progressionModalData?.benefitValue || '';
          const rewardIsEmpty = !rewardVal || rewardVal.toString().trim() === '0' || rewardVal.toString().trim().toLowerCase() === 'n/a' || rewardVal.toString().trim().toLowerCase() === 'nothing' || rewardVal.toString().trim().toLowerCase() === 'none';
          const incentiveVal = progressionModalData?.incentiveAmount || 0;
          const incentiveIsEmpty = !incentiveVal || parseFloat(incentiveVal.toString().replace(/[^0-9.]/g, '')) === 0;
          const noMedia = rewardIsEmpty && incentiveIsEmpty;

          return (
            <>
              {!noMedia && (
                <Box sx={{ position: 'relative', height: 220, bgcolor: '#f1f5f9' }}>
                   {progressionModalData && (
                     <img 
                        src={progressionModalData.type === 'Reward' 
                           ? getRewardImageSrc(progressionModalData.benefitValue)
                           : require('../../images/cash_bonus_inr_3d.png')} 
                        alt="Reward"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                     />
                   )}
                   <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                      <IconButton onClick={() => setProgressionModalData(null)} sx={{ bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' } }}><Close /></IconButton>
                   </Box>
                </Box>
              )}
              {noMedia && (
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                   <IconButton onClick={() => setProgressionModalData(null)} sx={{ bgcolor: 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' } }}><Close /></IconButton>
                </Box>
              )}
            </>
          );
        })()}
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>
            {progressionModalData?.rankName || 'Level Details'}
            {progressionModalData?.cardStatus === 'upcoming' && <Chip label="Upcoming" size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: '10px', fontWeight: 800 }} />}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
             Complete your business targets to unlock this {progressionModalData?.type}.
          </Typography>

          <Grid container spacing={3}>
             <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>REWARD / BENEFIT</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{progressionModalData?.rewardDescription || progressionModalData?.benefitLabel || 'N/A'}</Typography>
             </Grid>
             <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>INCENTIVE</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{progressionModalData?.incentiveAmount ? `₹${progressionModalData.incentiveAmount}` : '₹0'}</Typography>
             </Grid>
             <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>ELIGIBILITY</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: progressionModalData?.cardStatus === 'achieved' ? 'success.main' : 'warning.main' }}>
                   {progressionModalData?.cardStatus === 'achieved' ? 'Eligible' : 'In Progress'}
                </Typography>
             </Grid>
             
             {progressionModalData?.type === 'Incentive' ? (
                <Grid item xs={6}>
                   <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>PAYOUT DURATION</Typography>
                   <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {progressionModalData?.incentivePayoutMonths ? `${progressionModalData.incentivePayoutMonths} Months` : 'One-time'}
                   </Typography>
                </Grid>
             ) : (
                <Grid item xs={6}>
                   <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>EXPIRY DATE</Typography>
                   <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Dec 31, 2026</Typography>
                </Grid>
             )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          {progressionModalData?.cardStatus === 'achieved' ? (
            <Button variant="contained" color="success" fullWidth sx={{ py: 1.5, borderRadius: 2, fontWeight: 800, backgroundImage: 'linear-gradient(45deg, #10b981, #059669)', boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)' }}>
              Claim {progressionModalData?.type}
            </Button>
          ) : (
            <Button variant="contained" disabled fullWidth sx={{ py: 1.5, borderRadius: 2, fontWeight: 800 }}>
              Target Not Reached
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
